import * as React from "react"
import { useEffect, useImperativeHandle, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button, Input } from "./form-items"

interface CodeInputProps {
	value: string
	onChange: (value: string) => void
	onResend: () => any
	error?: string
	disabled?: boolean
	placeholder?: string
	maxLength?: number
	initialCountdown?: number // 初始倒计时时间，单位秒
	autoStartCountdown?: boolean // 是否自动开始倒计时
}

export interface CodeInputRef {
	startCountdown: () => void
}

const CodeInputComponent = React.forwardRef<CodeInputRef, CodeInputProps>(
	(
		{
			value,
			onChange,
			onResend,
			error,
			disabled = false,
			maxLength = 6,
			initialCountdown = 60, // 默认60秒
			autoStartCountdown = true, // 默认自动开始倒计时
			placeholder: customPlaceholder, // 将 props 中的 placeholder 重命名，以避免与解构中的默认值冲突
		},
		ref,
	) => {
		const { t } = useTranslation()
		const actualPlaceholder = customPlaceholder || t("placeholder.codeInput")
		const [countdown, setCountdown] = useState(0)
		const [isResending, setIsResending] = useState(false) // 添加状态来处理重新发送的加载状态
		const [hasSentCode, setHasSentCode] = useState(false) // 添加状态来跟踪是否已经发送过验证码

		// 处理重新发送
		const handleResend = async () => {
			setIsResending(true) // 开始重新发送
			try {
				const _result = await onResend()
				setHasSentCode(true) // 标记已经发送过验证码
			} catch (error) {
				console.error("重新发送失败:", error)
				// 可以在这里处理错误，例如显示 toast
			} finally {
				setIsResending(false) // 结束重新发送
			}
		}

		// 开始倒计时
		const startCountdown = () => {
			setCountdown(initialCountdown)
		}

		// 暴露方法给父组件
		useImperativeHandle(ref, () => ({
			startCountdown,
		}))

		// 组件挂载时自动开始倒计时
		useEffect(() => {
			if (autoStartCountdown) {
				startCountdown()
			}
		}, [autoStartCountdown])

		// 倒计时效果
		useEffect(() => {
			if (countdown <= 0) return

			const timer = setInterval(() => {
				setCountdown((prev) => {
					if (prev <= 1) {
						clearInterval(timer)
						return 0
					}
					return prev - 1
				})
			}, 1000)

			return () => clearInterval(timer)
		}, [countdown])

		const isButtonDisabled = countdown > 0 || disabled || isResending

		return (
			<div className="space-y-2">
				<div className="flex items-center space-x-2">
					<Input
						id="code"
						type="text"
						placeholder={actualPlaceholder}
						value={value}
						onChange={(e) => onChange(e.target.value)}
						disabled={disabled}
						maxLength={maxLength}
						className="grow placeholder:text-xs! md:placeholder:text-base!"
					/>
					<Button
						type="button"
						variant="default"
						className="mt-0 h-10 w-20 disabled:bg-[#eee]! disabled:text-black/80! disabled:opacity-100! md:mt-0 md:h-12 md:w-[94px]"
						size="lg"
						onClick={handleResend}
						disabled={isButtonDisabled}
					>
						{isResending
							? t("button.sending")
							: countdown > 0
								? t("text.secondsWithCount", { count: countdown })
								: hasSentCode
									? t("button.resend")
									: t("button.sendCode")}
					</Button>
				</div>
				{error && (
					<p className="text-destructive text-sm leading-[1.5]">{error}</p>
				)}
			</div>
		)
	},
)

// 添加displayName
CodeInputComponent.displayName = "CodeInput"

export const CodeInput = CodeInputComponent
